import { useState, type FormEvent } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ApiError, api } from '../../api/client';
import type { Me } from '../../api/types';
import { IconCheckCircle, IconInfo } from '../../shared/icons';
import { AuthLayout } from './AuthLayout';

interface SetPasswordPageProps {
  token: string;
  /** Which link brought the user here — it only changes the wording. */
  kind: 'invite' | 'reset';
  onSignedIn: (me: Me) => void;
  onGoToSignIn: () => void;
}

export function SetPasswordPage({ token, kind, onSignedIn, onGoToSignIn }: SetPasswordPageProps) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [mismatch, setMismatch] = useState(false);

  // The link is resolved before anything is typed, so an expired or spent one
  // says so straight away rather than after the user has chosen a password.
  const link = useQuery({
    queryKey: ['link', token],
    queryFn: () => api.inspectLink(token),
    retry: false,
  });

  const submit = useMutation({
    mutationFn: () => api.setPasswordViaLink(token, password),
    onSuccess: onSignedIn,
  });

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (password !== confirm) {
      setMismatch(true);
      return;
    }
    setMismatch(false);
    submit.mutate();
  };

  const invited = kind === 'invite';
  const heading = invited ? 'Choose your password' : 'Set a new password';

  if (link.isPending) {
    return (
      <AuthLayout heading={heading} kicker={invited ? 'Invitation' : 'Password reset'}>
        <div className="auth-card">
          <span className="cell-muted">Checking the link…</span>
        </div>
      </AuthLayout>
    );
  }

  if (link.isError) {
    const message =
      link.error instanceof ApiError ? link.error.message : 'This link could not be checked.';
    return (
      <AuthLayout heading="Link expired" kicker={invited ? 'Invitation' : 'Password reset'}>
        <div className="auth-card">
          <div className="notice notice-error" role="alert">
            <IconInfo size={14} />
            <span>{message}</span>
          </div>
          <p className="field-help">
            Invitation links expire after seven days and reset links after an hour, and each one
            works only once.
          </p>
          <button className="btn btn-block" onClick={onGoToSignIn}>
            Back to sign in
          </button>
        </div>
      </AuthLayout>
    );
  }

  const error = submit.error instanceof ApiError ? submit.error : null;

  return (
    <AuthLayout heading={heading} kicker={invited ? 'Invitation' : 'Password reset'}>
      <form className="auth-card" onSubmit={handleSubmit} noValidate>
        <div className="notice">
          <IconCheckCircle size={14} />
          <span>
            {invited ? 'Setting up' : 'Resetting'} <strong>{link.data.email}</strong>
          </span>
        </div>

        {error && (
          <div className="notice notice-error" role="alert">
            <IconInfo size={14} />
            <span>{error.message}</span>
          </div>
        )}
        {mismatch && (
          <div className="notice notice-error" role="alert">
            <IconInfo size={14} />
            <span>The two passwords do not match.</span>
          </div>
        )}

        {/* A hidden username field is what lets a password manager file the
            saved entry under the right account. */}
        <input type="hidden" name="username" autoComplete="username" value={link.data.email} readOnly />

        <div className="field">
          <label className="lbl" htmlFor="new-password">
            <span className="lbl-text">Password</span>
          </label>
          <input
            id="new-password"
            className="inp"
            type="password"
            autoComplete="new-password"
            minLength={12}
            required
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <span className="field-help">
            At least 12 characters. Length is what protects an account, so a long phrase beats a
            short tangle of symbols.
          </span>
        </div>

        <div className="field">
          <label className="lbl" htmlFor="confirm-password">
            <span className="lbl-text">Confirm password</span>
          </label>
          <input
            id="confirm-password"
            className={`inp${mismatch ? ' inp-invalid' : ''}`}
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </div>

        <button className="btn btn-primary btn-lg btn-block" type="submit" disabled={submit.isPending}>
          {submit.isPending ? 'Saving…' : invited ? 'Set password and sign in' : 'Set new password'}
        </button>

        {!invited && (
          <p className="field-help">
            Setting a new password signs out every other device.
          </p>
        )}
      </form>
    </AuthLayout>
  );
}
